import CodeBlockModal from "@/components/CodeBlockModal";
import { RequestDisplayChatView } from "@/components/RequestDisplay/ChatView";
import { DecisionAnswersResponse } from "@/components/RequestDisplay/DecisionAnswersResponse";
import {
  buildTraceTranscriptBlocks,
  getServerToolCallCount,
  getTraceConversationMessages,
  getTraceSystemMessage,
  getTraceTranscriptStats,
} from "@/components/RequestDisplay/ChatView/utils";
import {
  ChatRequest,
  ChatResponse,
  ImageResponse,
  NoResponse,
} from "@/components/RequestDisplay/RequestDisplayContent";
import { RequestDisplayChatLoadingState } from "@/components/RequestDisplay/RequestDisplayChatLoadingState";
import { RequestDisplayModeSwitcher } from "@/components/RequestDisplay/RequestDisplayModeSwitcher";
import RequestDisplaySignalBubbles from "@/components/RequestDisplay/RequestDisplaySignalBubbles";
import { RequestDisplayTemplateView } from "@/components/RequestDisplay/RequestDisplayTemplateView";
import {
  RequestDisplaySource,
  getChatRequestDisplayState,
  getRequestOutputMedia,
} from "@/components/RequestDisplay/RequestDisplayUtils";
import { RequestInfoBlock } from "@/components/RequestInfoBlock";
import { TextCopyBlock } from "@/components/TextCopyBlock";
import { useRequestDisplayState } from "@/components/RequestDisplay/useRequestDisplayState";
import { getStringContent } from "@/utils/utils";
import NotFoundMessage from "@/components/ui/not-found";
import ErrorDisplay from "@/components/ui/ErrorDisplay";
import { RequestErrorBanner } from "@/components/ui/RequestErrorBanner";
import { Skeleton } from "@/components/ui/skeleton.tsx";

const ErrorBanner = ({ title = "Error Displaying Request" }: { title?: string }) => (
  <ErrorDisplay title={title} className="mb-4">
    <span>
      Please see{" "}
      <a
        href="https://docs.promptlayer.com/reference/log-request"
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium underline hover:text-red-800"
      >
        the docs
      </a>{" "}
      for more information.
    </span>
  </ErrorDisplay>
);

const DisplayErrorState = ({ className, label }: { className: string; label: string }) => (
  <div className={className}>
    <span className="italic text-gray-500">{label}</span>
  </div>
);

const PromptLoadingState = () => (
  <div className="space-y-1">
    <div className="relative space-y-2 overflow-y-auto rounded-sm border border-gray-300 bg-white p-4">
      <Skeleton height="14px" width="70%" />
      <Skeleton height="14px" width="45%" />
    </div>
    <div className="relative space-y-2 overflow-y-auto rounded-sm border border-gray-300 bg-white p-4">
      <Skeleton height="14px" width="85%" />
      <Skeleton height="14px" width="60%" />
      <Skeleton height="14px" width="95%" />
    </div>
  </div>
);

const ResponseLoadingState = () => (
  <div className="relative h-full overflow-y-auto rounded-sm border border-gray-300 bg-gray-100 p-4">
    <div className="flex h-full flex-col gap-4">
      <div className="space-y-2">
        <Skeleton height="14px" width="90%" />
        <Skeleton height="14px" width="100%" />
        <Skeleton height="14px" width="70%" />
      </div>
      <div className="space-y-2">
        <Skeleton height="14px" width="85%" />
        <Skeleton height="14px" width="60%" />
        <Skeleton height="14px" width="95%" />
      </div>
      <div className="space-y-2">
        <Skeleton height="14px" width="55%" />
        <Skeleton height="14px" width="40%" />
      </div>
    </div>
  </div>
);

const RequestDisplay = ({
  requestId,
  requestPreview,
  requestBulk,
}: {
  requestId: string;
  requestPreview?: RequestDisplaySource;
  requestBulk?: RequestDisplaySource;
}) => {
  const {
    bulkLoaded,
    cacheCreationInputTokens,
    cacheReadInputTokens,
    chatBoxOpenStates,
    chatResponseTextBlock,
    choice,
    choicesCount,
    choiceResponse,
    displayError,
    engine,
    fullScreenModalText,
    handleCollapseAll,
    handleExpandAll,
    handleToggleRequestDisplayView,
    hasNoResponse,
    inputTokens,
    isChat,
    isChatView,
    isMedia,
    jsonSchema,
    outputTokens,
    requestInfo,
    requestResponse,
    requestText,
    responseMessageOverride,
    setChatBoxOpenStates,
    setChoice,
    setShowRequestModal,
    setShowFullRequest,
    setShowFullResponse,
    setShowResponseModal,
    showFullRequest,
    showFullResponse,
    showRequestModal,
    showResponseModal,
    template,
  } = useRequestDisplayState({
    requestId,
    requestPreview,
    requestBulk,
  });

  if (!requestInfo) {
    return (
      <NotFoundMessage
        title="Log not found"
        subtitle="The LLM log you are looking for does not exist or you do not have access to it."
        backLinkMessage="Go Home"
        backLink="/"
      />
    );
  }

  const multipleChoiceSelector =
    choicesCount > 1 ? (
      <span className="flex gap-x-1 pr-1">
        {Array.from({ length: choicesCount }, (_, index) => (
          <button
            type="button"
            className={`mt-1 rounded-sm border border-gray-300 px-2 py-1 font-mono text-xs text-gray-600 ${
              choice === index
                ? "border-gray-400 bg-gray-100"
                : "hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900"
            }`}
            key={index}
            onClick={() => setChoice(index)}
          >
            {index + 1}
          </button>
        ))}
      </span>
    ) : null;

  const promptDisplay = (() => {
    if (!requestBulk) {
      return <PromptLoadingState />;
    }

    if (displayError.hasError) {
      return (
        <DisplayErrorState
          className="rounded-sm border border-gray-300 bg-white p-4"
          label="Error displaying"
        />
      );
    }

    try {
      if (isChat && !showFullRequest) {
        return (
          <ChatRequest
            jsonSchema={jsonSchema}
            requestInfo={requestInfo}
            isOpenStates={chatBoxOpenStates}
            setIsOpenStates={setChatBoxOpenStates}
            requestId={requestId}
          />
        );
      }

      return (
        <TextCopyBlock
          text={requestText}
          code={showFullRequest}
          inputVariables={requestInfo.prompt_input_variables}
        />
      );
    } catch (error) {
      console.error("Error rendering prompt display:", error); // eslint-disable-line no-console
      return (
        <DisplayErrorState
          className="rounded-sm border border-gray-300 bg-white p-4"
          label="Error displaying"
        />
      );
    }
  })();

  const responseDisplay = (() => {
    if (!requestBulk) {
      return <ResponseLoadingState />;
    }

    if (displayError.hasError) {
      return (
        <DisplayErrorState
          className="rounded-sm border border-gray-300 bg-gray-100 p-4"
          label="Error displaying"
        />
      );
    }

    try {
      if (hasNoResponse) {
        return <NoResponse />;
      }

      if (requestInfo?.decision_answers?.length && !showFullResponse) {
        return <DecisionAnswersResponse answers={requestInfo.decision_answers} />;
      }

      if (isChat && !showFullResponse) {
        return (
          <ChatResponse
            requestResponse={choiceResponse ?? requestResponse}
            requestInfo={requestInfo}
          />
        );
      }

      if (isMedia && !showFullResponse) {
        return <ImageResponse requestInfo={requestInfo} />;
      }

      return (
        <TextCopyBlock
          text={chatResponseTextBlock}
          code={showFullResponse}
          highlight={!showFullResponse}
        />
      );
    } catch (error) {
      console.error("Error rendering response display:", error); // eslint-disable-line no-console
      return (
        <DisplayErrorState
          className="rounded-sm border border-gray-300 bg-gray-100 p-4"
          label="Error displaying"
        />
      );
    }
  })();

  const chatViewDisplay = (() => {
    if (!requestBulk) {
      return <RequestDisplayChatLoadingState />;
    }

    if (displayError.hasError) {
      return (
        <DisplayErrorState
          className="rounded-xl border border-gray-200 bg-white p-4"
          label="Error displaying conversation"
        />
      );
    }

    if (template?.type === "chat") {
      return (
        <RequestDisplayChatView
          chatTemplate={template}
          responseMessageOverride={responseMessageOverride}
          decisionAnswers={requestInfo?.decision_answers}
        />
      );
    }

    return null;
  })();

  const chatTranscriptStats =
    template?.type === "chat"
      ? (() => {
          const { allMessages } = getChatRequestDisplayState(template, responseMessageOverride);
          return getTraceTranscriptStats(
            buildTraceTranscriptBlocks(getTraceConversationMessages(allMessages)),
            { serverToolCallCount: getServerToolCallCount(requestResponse) },
          );
        })()
      : undefined;

  const chatSystemMessage =
    template?.type === "chat"
      ? (() => {
          const { allMessages } = getChatRequestDisplayState(template, responseMessageOverride);
          const systemMessage = getTraceSystemMessage(allMessages);
          return systemMessage ? getStringContent(systemMessage) : undefined;
        })()
      : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden pb-4">
      <CodeBlockModal
        showModal={showRequestModal || showResponseModal}
        setShowModal={showRequestModal ? setShowRequestModal : setShowResponseModal}
        text={fullScreenModalText}
        isCode={showRequestModal ? showFullRequest : showFullResponse}
        highlight={showResponseModal && !showFullResponse}
        forceMonoSpace
        inputVariables={requestInfo.prompt_input_variables}
        media={
          showResponseModal && !showFullResponse
            ? getRequestOutputMedia(requestInfo, responseMessageOverride)
            : []
        }
      />
      <RequestInfoBlock
        key={requestId}
        requestId={requestId}
        requestInfo={requestInfo}
        displayName={requestInfo.prompt_version?.metadata?.model?.model_config_display_name ?? ""}
        requestText={requestText}
        inputTokens={inputTokens}
        outputTokens={outputTokens}
        cacheReadInputTokens={cacheReadInputTokens}
        cacheCreationInputTokens={cacheCreationInputTokens}
        response_format={requestInfo.response_format?.type ?? undefined}
        engine={engine ?? ""}
        provider={requestInfo.provider_type ?? ""}
        disabled={displayError.hasError}
      />
      {displayError.hasError && <ErrorBanner title={displayError.title} />}
      {requestInfo.status && requestInfo.status !== "SUCCESS" && (
        <RequestErrorBanner
          status={requestInfo.status}
          errorType={requestInfo.error_type ?? null}
          errorMessage={requestInfo.error_message ?? null}
        />
      )}
      <RequestDisplaySignalBubbles metadata={requestInfo.metadata} />
      {isChat && (
        <div className="flex w-full items-center justify-between">
          <div className="shrink-0">
            {multipleChoiceSelector && <div className="shrink-0">{multipleChoiceSelector}</div>}
          </div>
          <div className="flex min-w-0 flex-1 justify-end">
            <RequestDisplayModeSwitcher
              chatStats={chatTranscriptStats}
              systemMessage={chatSystemMessage}
              isChatView={isChatView}
              onSelectView={view => {
                if ((view === "chat") !== isChatView) {
                  handleToggleRequestDisplayView();
                }
              }}
            />
          </div>
        </div>
      )}
      {isChatView ? (
        <div className="min-h-0 flex-1 overflow-hidden">{chatViewDisplay}</div>
      ) : (
        <RequestDisplayTemplateView
          bulkLoaded={bulkLoaded}
          hasNoResponse={hasNoResponse}
          isChat={isChat}
          multipleChoiceSelector={multipleChoiceSelector}
          promptDisplay={promptDisplay}
          responseDisplay={responseDisplay}
          showFullRequest={showFullRequest}
          showFullResponse={showFullResponse}
          onCollapseAll={handleCollapseAll}
          onExpandAll={handleExpandAll}
          onOpenRequestModal={() => setShowRequestModal(true)}
          onOpenResponseModal={() => setShowResponseModal(true)}
          onToggleShowFullRequest={() => setShowFullRequest(current => !current)}
          onToggleShowFullResponse={() => setShowFullResponse(current => !current)}
        />
      )}
    </div>
  );
};

export default RequestDisplay;
